import React from "react";
import { FiArrowLeft, FiCalendar, FiLoader, FiRefreshCcw, FiDownload } from "react-icons/fi";
import { useCashFlowDashboard } from "./hooks/useCashFlowDashboard";
import { formatDashboardTitle, YEAR } from "./utils";
import ChartCard from "./components/ChartCard";
import OverviewCards from "./components/OverviewCards";
import OverviewTrendChart from "./components/OverviewTrendChart";
import OverviewInflowChart from "./components/OverviewInflowChart";
import OverviewOutflowChart from "./components/OverviewOutflowChart";
import OverviewWeeklyChart from "./components/OverviewWeeklyChart";
import SummaryReportTable from "./components/SummaryReportTable";
import MisReportTable from "./components/MisReportTable";

const EDACashFlowDashboard = () => {
  const {
    navigate, location, dashboard, selectedProcess, monthPeriods, weekPeriods,
    selectedMonthPeriod, setSelectedMonthPeriod, selectedWeekPeriod, setSelectedWeekPeriod,
    selectedYear, setSelectedYear, misSelectedMonthPeriod, setMisSelectedMonthPeriod,
    misSelectedYear, setMisSelectedYear, misReportData, misLoading, misError,
    activeTab, setActiveTab, overviewMonths, overviewSelectedMonth, setOverviewSelectedMonth,
    overviewSelectedYear, setOverviewSelectedYear, overviewData, overviewLoading,
    overviewError, dashboardData, loading, error, handleReset, handleOverviewReset,
    handleMisReset, hasSummaryData
  } = useCashFlowDashboard();
  const title = formatDashboardTitle(dashboard?.eda_name || "Cash Flow");
  const TabButton = ({ label, tabName }) => (
    <button type="button" onClick={() => setActiveTab(tabName)}
      className={`px-4 py-3 text-sm font-semibold transition-colors duration-200 focus:outline-none ${activeTab === tabName ? "border-b-2 border-blue-600 text-blue-600" : "text-slate-500 hover:text-slate-800"}`}
    >{label}</button>
  );
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="mt-1 text-sm text-slate-500">
              Process: {formatDashboardTitle(selectedProcess)} | Status:{" "}
              {dashboard?.edaStatus || "Available"}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
        </div>

        <div className="border-b border-slate-200">
          <div className="flex items-center -mb-px">
            <TabButton label="Overview" tabName="overview" />
            <TabButton label="Summary" tabName="summary" />
            <TabButton label="MIS Report" tabName="mis" />
          </div>
        </div>

        {activeTab === "overview" && (
          <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={overviewSelectedYear}
                  onChange={(event) =>
                    setOverviewSelectedYear(Number(event.target.value))
                  }
                  disabled={overviewLoading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEAR.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Month
                </span>
                <select
                  value={overviewSelectedMonth?.value || ""}
                  onChange={(e) => {
                    const period = overviewMonths.find(
                      (p) => p.value == e.target.value,
                    );
                    setOverviewSelectedMonth(period);
                  }}
                  disabled={overviewLoading || overviewMonths.length === 0}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {overviewMonths.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleOverviewReset}
                disabled={overviewLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {overviewLoading ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                  <FiCalendar className="h-4 w-4" />
                  Current period: {overviewSelectedMonth?.label || "N/A"}{" "}
                  {overviewSelectedYear}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "summary" && (
          <>
            {!location.state ? (
              <div className="pt-4 rounded-xl border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Open this page from the dashboard list so the selected dashboard
                context is available.
              </div>
            ) : (
              <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Year
                    </span>
                    <select
                      value={selectedYear}
                      onChange={(event) =>
                        setSelectedYear(Number(event.target.value))
                      }
                      disabled={loading}
                      className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {YEAR.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Monthly Period
                    </span>
                    <select
                      value={selectedMonthPeriod?.value || ""}
                      onChange={(e) => {
                        const period = monthPeriods.find(
                          (p) => p.value == e.target.value,
                        );
                        setSelectedMonthPeriod(period);
                      }}
                      disabled={loading || monthPeriods.length === 0}
                      className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {monthPeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-400">
                      Weekly Period
                    </span>
                    <select
                      value={selectedWeekPeriod?.value || ""}
                      onChange={(e) => {
                        const period = weekPeriods.find(
                          (p) => p.value == e.target.value,
                        );
                        setSelectedWeekPeriod(period);
                      }}
                      disabled={loading || weekPeriods.length === 0}
                      className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                    >
                      {weekPeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
                  >
                    <FiRefreshCcw className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1">
                  {loading ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                      <FiLoader className="h-4 w-4 animate-spin" />
                      Updating dashboard...
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                      <FiCalendar className="h-4 w-4" />
                      Current period: {selectedMonthPeriod?.label || "N/A"}{" "}
                      {selectedYear}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "mis" && (
          <div className="flex flex-col gap-3 pt-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">Year</span>
                <select
                  value={misSelectedYear}
                  onChange={(event) =>
                    setMisSelectedYear(Number(event.target.value))
                  }
                  disabled={misLoading}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {YEAR.map((year) => (
                    <option key={year.value} value={year.value}>
                      {year.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-400">
                  Monthly Period
                </span>
                <select
                  value={misSelectedMonthPeriod?.value || ""}
                  onChange={(e) => {
                    const period = monthPeriods.find(
                      (p) => p.value == e.target.value,
                    );
                    setMisSelectedMonthPeriod(period);
                  }}
                  disabled={misLoading || monthPeriods.length === 0}
                  className="min-w-[180px] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 disabled:cursor-not-allowed disabled:bg-slate-50"
                >
                  {monthPeriods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleMisReset}
                disabled={misLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-50"
              >
                <FiRefreshCcw className="h-3.5 w-3.5" />
                Reset
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {misLoading ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
                  <FiLoader className="h-4 w-4 animate-spin" />
                  Updating report...
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                  <FiCalendar className="h-4 w-4" />
                  Current period: {misSelectedMonthPeriod?.label || "N/A"}{" "}
                  {misSelectedYear}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {activeTab === "summary" && (
        <div className="pt-4 space-y-4">
          {loading && !dashboardData ? (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading Summary...</span>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          ) : hasSummaryData ? (
            <div className="space-y-6">
              {Object.entries(dashboardData).map(([title, data]) => (
                <SummaryReportTable key={title} title={title} data={data} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No Summary Data
              </h3>
              <p className="mt-2">
                No summary data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "overview" && (
        <div className="pt-4 space-y-4">
          {overviewLoading && (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading Overview...</span>
            </div>
          )}
          {overviewError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {overviewError}
            </div>
          )}
          {!overviewLoading && !overviewError && overviewData && (
            <div className="space-y-6">
              <OverviewCards data={overviewData} />
              <ChartCard title="Monthly Trend - Collections vs Payments">
                <OverviewTrendChart data={overviewData.chart1} />
              </ChartCard>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="Cash Inflow Breakup">
                  <OverviewInflowChart data={overviewData.chart2} />
                </ChartCard>
                <ChartCard title="Cash Outflow Breakup">
                  <OverviewOutflowChart data={overviewData.chart4} />
                </ChartCard>
              </div>
              <ChartCard title="Weekly Trend - Collections vs Payments">
                <OverviewWeeklyChart data={overviewData.chart3} />
              </ChartCard>
              {overviewData.excelFiles &&
                overviewData.excelFiles[0]?.xlsLink && (
                  <div className="flex justify-end pt-2">
                    <a
                      href={overviewData.excelFiles[0].xlsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-[#0060D0] px-4 py-2 text-sm font-semibold !text-white shadow-sm transition hover:bg-[#0050b0]"
                    >
                      <FiDownload className="h-4 w-4" />
                      {overviewData.excelFiles[0].id ||
                        "Download Consolidated Report"}
                    </a>
                  </div>
                )}
            </div>
          )}
          {!overviewLoading && !overviewError && !overviewData && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No Overview Data
              </h3>
              <p className="mt-2">
                No overview data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "mis" && (
        <div className="pt-4 space-y-4">
          {misLoading ? (
            <div className="flex items-center justify-center p-10">
              <FiLoader className="h-8 w-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading MIS Report...</span>
            </div>
          ) : misError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {misError}
            </div>
          ) : misReportData && Object.keys(misReportData).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(misReportData).map(([title, data]) => (
                <MisReportTable key={title} title={title} data={data} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-500">
              <h3 className="text-lg font-semibold text-slate-700">
                No MIS Report Data
              </h3>
              <p className="mt-2">
                No MIS report data available for the selected period.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EDACashFlowDashboard;
